import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/sections/CTASection";
import { industries } from "@/data/content";
import { industriesSchema } from "@/data/seo";

const IND_CRUMBS = [{ name: "Industries", path: "/industries" }];
const IND_SCHEMA = [industriesSchema()];

function IndustryMedia({ ind, videoRef }) {
  return (
    <div
      data-testid={`industry-media-${ind.slug}`}
      className="relative -order-1 aspect-[16/10] overflow-hidden md:order-none md:col-span-2 md:aspect-[4/3]"
    >
      {ind.video ? (
        <video
          ref={videoRef}
          src={ind.video}
          poster={ind.image}
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
        />
      ) : (
        <img
          src={ind.image}
          alt={ind.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
        />
      )}
    </div>
  );
}

function IndustryRow({ ind, i }) {
  const videoRef = useRef(null);
  const play = () => videoRef.current?.play().catch(() => {});
  const stop = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };
  const hover = ind.video
    ? { onMouseEnter: play, onMouseLeave: stop, onTouchStart: play, onTouchEnd: stop, onTouchCancel: stop }
    : {};
  return (
    <motion.div
      id={ind.slug}
      className="scroll-mt-20 md:scroll-mt-24"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.6, delay: (i % 4) * 0.05, ease: [0.76, 0, 0.24, 1] }}
    >
      <Link
        to="/contact"
        data-testid={`industry-${ind.slug}`}
        {...hover}
        className="group grid grid-cols-1 items-center gap-4 border-b border-line py-8 transition-colors hover:bg-surface md:grid-cols-12 md:gap-8 md:py-10"
      >
        <span className="font-serif text-xl italic text-accent md:col-span-1">{String(i + 1).padStart(2, "0")}</span>
        {ind.image && <IndustryMedia ind={ind} videoRef={videoRef} />}
        <h2 className="font-display text-3xl font-extrabold text-ink transition-transform duration-500 group-hover:translate-x-2 md:col-span-3 md:text-4xl">
          {ind.name}
        </h2>
        <p className="text-base text-muted md:col-span-5">{ind.blurb}</p>
        <ArrowUpRight size={22} className="hidden text-ink opacity-0 transition-opacity group-hover:opacity-100 md:col-span-1 md:block md:justify-self-end" />
      </Link>
    </motion.div>
  );
}


export default function Industries() {
  useSeo({
    title: "Industries: Marketing for 12 Sectors Across India",
    description:
      "Brands built across automobiles, beauty, education, fashion, jewellery, F&B, hospitality, real estate, interiors and exhibitions by an integrated agency in Gurgaon.",
    path: "/industries",
    breadcrumbs: IND_CRUMBS,
    schema: IND_SCHEMA,
  });
  return (
    <>
      <PageHeader
        eyebrow="Industries"
        titleLines={["Sectors we", "know deeply."]}
        intro="Category fluency shortens the distance between insight and impact. These are the worlds we've worked across."
      />
      <section className="mx-auto max-w-[1600px] px-5 py-8 sm:py-10 md:py-12 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="border-t border-line">
          {industries.map((ind, i) => (
            <IndustryRow key={ind.slug} ind={ind} i={i} />
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
